"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog as DialogRadix } from "radix-ui";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Backdrop } from "@/components/common/Backdrop";
import { useDialogContentAnimation } from "@/hooks/animations/useDialogAnimation";
import { twMergeClasses } from "@/utils/twMergeClasses";
const DialogAnimContext = createContext({
  isExiting: false,
  shouldRender: false,
  onExitComplete: () => {}
});
function DialogRoot(t0) {
  const $ = _c(25);
  let children;
  let defaultOpen;
  let onOpenChange;
  let open;
  let props;
  if ($[0] !== t0) {
    ({
      open,
      onOpenChange,
      defaultOpen,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = defaultOpen;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = props;
  } else {
    children = $[1];
    defaultOpen = $[2];
    onOpenChange = $[3];
    open = $[4];
    props = $[5];
  }
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen || false);
  const currentOpen = isControlled ? open : internalOpen;
  const [renderState, setRenderState] = useState(currentOpen);
  const [isExiting, setIsExiting] = useState(false);
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setRenderState(false);
      setIsExiting(false);
    };
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleExitComplete = t1;
  let t2;
  if ($[7] !== isControlled || $[8] !== onOpenChange) {
    t2 = status => {
      if (!isControlled) {
        setInternalOpen(status);
      }
      if (onOpenChange) {
        onOpenChange(status);
      }
    };
    $[7] = isControlled;
    $[8] = onOpenChange;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleOpenChange = t2;
  let t3;
  let t4;
  if ($[10] !== currentOpen || $[11] !== renderState) {
    t3 = () => {
      if (currentOpen) {
        setRenderState(true);
        setIsExiting(false);
      } else {
        if (renderState) {
          setIsExiting(true);
        }
      }
    };
    t4 = [currentOpen, renderState];
    $[10] = currentOpen;
    $[11] = renderState;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== isExiting || $[15] !== renderState) {
    t5 = {
      isExiting,
      shouldRender: renderState,
      onExitComplete: handleExitComplete
    };
    $[14] = isExiting;
    $[15] = renderState;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== children || $[18] !== handleOpenChange || $[19] !== props || $[20] !== renderState) {
    t6 = <DialogRadix.Root open={renderState} onOpenChange={handleOpenChange} {...props}>{children}</DialogRadix.Root>;
    $[17] = children;
    $[18] = handleOpenChange;
    $[19] = props;
    $[20] = renderState;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <DialogAnimContext.Provider value={t5}>{t6}</DialogAnimContext.Provider>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
interface DialogOverlayProps extends DialogRadix.DialogOverlayProps {
  ref?: React.Ref<HTMLDivElement>;
}
export function DialogOverlay(t0) {
  const $ = _c(11);
  let className;
  let props;
  if ($[0] !== t0) {
    const {
      className: t1,
      ref,
      ...t2
    } = t0;
    className = t1;
    props = t2;
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    isExiting,
    shouldRender
  } = useContext(DialogAnimContext);
  if (!shouldRender) {
    return null;
  }
  const t1 = !isExiting;
  let t2;
  if ($[3] !== className) {
    t2 = twMergeClasses("z-50 backdrop-blur-sm", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Backdrop isOpen={t1} className={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props || $[9] !== t3) {
    t4 = <DialogRadix.Overlay asChild={true} {...props}>{t3}</DialogRadix.Overlay>;
    $[8] = props;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
interface DialogContentProps extends DialogRadix.DialogContentProps {
  ref?: React.Ref<HTMLDivElement>;
}
export function DialogContent(t0) {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  const {
    isExiting,
    onExitComplete,
    shouldRender
  } = useContext(DialogAnimContext);
  const internalRef = useRef(null);
  useDialogContentAnimation(isExiting, internalRef, onExitComplete);
  let t1;
  if ($[5] !== ref) {
    t1 = node => {
      if (node) {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else {
          if (ref) {
            ref.current = node;
          }
        }
      }
    };
    $[5] = ref;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const setRefs = t1;
  if (!shouldRender) {
    return null;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogOverlay />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== className) {
    t3 = twMergeClasses("flex flex-col z-50 fixed left-1/2 top-1/2 max-w-xl md:min-w-lg w-[80%] max-h-dvh -translate-x-1/2 -translate-y-1/2 rounded-lg md:p-6 p-4 border border-gray-200 bg-white gap-4", className);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== props || $[12] !== setRefs || $[13] !== t3) {
    t4 = <>{t2}<DialogRadix.Content ref={setRefs} className={t3} {...props}>{children}</DialogRadix.Content></>;
    $[10] = children;
    $[11] = props;
    $[12] = setRefs;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export const Dialog = {
  Root: DialogRoot,
  Trigger: DialogRadix.Trigger,
  Portal: DialogRadix.Portal,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Title: DialogRadix.Title,
  Description: DialogRadix.Description,
  Close: DialogRadix.Close
};